// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for recording asset disposal (scrapping).
 * Provides functionality to mark assets as scrapped, including
 * the disposal date, any recovery value, and the reason for disposal.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetSchema } from '@/asset/api/types';
import { Currency } from '@/currency/api/types';
import { serializeCurrencyForAPI } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for validating the API response when scrapping an asset.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
const responseSchema = z
  .object({ data: assetSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

/**
 * Parameters required for recording asset disposal.
 * @typedef {Object} ScrapAssetParameters
 * @property {string} date - ISO formatted date when the asset was disposed of
 * @property {Currency} [price] - Optional recovery value, in the asset's own currency
 * @property {string | null} [reason] - Optional explanation for the disposal
 */
export interface ScrapAssetParameters {
  date: DateTime;
  price?: Currency;
  reason?: string | null;
}

/**
 * Records the disposal (scrapping) of an asset.
 * This function handles the API call to mark an asset as scrapped and validates
 * the response data using Zod schemas. It records when and why the asset
 * was taken out of service, along with any financial recovery.
 *
 * @param key - Unique identifier of the asset to scrap
 * @param parameters - Object containing the disposal details
 * @returns Promise resolving to the updated asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function scrapAsset(
  key: string,
  parameters: ScrapAssetParameters
) {
  const url = `/v1/asset/${key}/scrap`;
  const response = await api.post(url, {
    disposalDate: serializeDateForAPI(parameters.date),
    disposalPrice: parameters.price
      ? serializeCurrencyForAPI(parameters.price)
      : undefined,
    disposalReason: parameters.reason,
  });

  return parseResponse(responseSchema, response.data, {
    schemaName: 'ScrapAsset',
    endpoint: url,
  });
}
